import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { money } from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { fullName } from './clientShared';
import { useFeatures } from '../../context/FeatureContext';

export default function ClientQuoteForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const record = useClientRecord(id);
  const features = useFeatures();
  const [lines, setLines] = useState([]);
  const [funds, setFunds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    clientStatus: 'All', quoteExpiry: '', servicePeriodFrom: '', servicePeriodTo: '', fund: '',
    addQuoteTerms: features.add_quote_terms,
    quoteTermsText: features.default_quote_terms,
  });

  useEffect(() => {
    setForm(f => ({
      ...f,
      addQuoteTerms: features.add_quote_terms,
      quoteTermsText: features.default_quote_terms,
    }));
  }, [features.add_quote_terms, features.default_quote_terms]);

  useEffect(() => {
    setFunds((record.client && record.client.funds) || []);
  }, [record.client]);

  async function saveQuote() {
    if (!lines.length) return toast.error('Add at least one line item before saving');
    if (!form.servicePeriodFrom || !form.servicePeriodTo) return toast.error('Service period dates are required');

    const totalValue = lines.reduce((sum, l) =>
      sum + ((parseFloat(l.quantity || 0)) * (parseFloat(l.pricePerUnit || 0))), 0
    );

    setSaving(true);
    try {
      const { data } = await api.post('/service-agreements', {
        clientId: id,
        agreementNumber: `QUOTE-${Date.now()}`,
        startDate: form.servicePeriodFrom,
        endDate: form.servicePeriodTo,
        totalValue,
        status: 'draft',
        notes: form.addQuoteTerms ? form.quoteTermsText : '',
        signedByClient: false,
        items: lines.map(l => ({
          supportItemNumber: l.pricebook || null,
          supportItemName: l.description || null,
          hoursPerWeek: parseFloat(l.quantity) || null,
          rate: parseFloat(l.pricePerUnit) || null,
          totalAmount: (parseFloat(l.quantity || 0)) * (parseFloat(l.pricePerUnit || 0)),
          fundId: form.fund || null,
        })),
      });
      toast.success(`Quote saved (#${data.agreement_number || 'draft'})`);
      navigate('/service-agreements');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  }

  const addLine = () => setLines(l => [...l, { pricebook: '', rateGroup: '', ratio: '', description: '', discountPct: '', discountDollar: '', recurrence: '', quantity: '', pricePerUnit: '' }]);
  const removeLine = i => setLines(l => l.filter((_, j) => j !== i));
  const setLine = (i, k, v) => setLines(l => l.map((x, j) => j === i ? { ...x, [k]: v } : x));

  return (
    <ClientFrame record={record}>
      <h2 className="text-base font-semibold text-slate-900">New {(features.quote_title || 'quote').toLowerCase()}</h2>

      <div className="card p-4 sm:p-6 space-y-6">
        {/* Header fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className="input-label">Client Status</label>
            <select className="input-field" value={form.clientStatus} onChange={e => setForm(f => ({ ...f, clientStatus: e.target.value }))}>
              <option>All</option><option>Active</option><option>Inactive</option>
            </select>
          </div>
          <div>
            <label className="input-label">Client</label>
            <input className="input-field" value={fullName(record.client)} readOnly placeholder="Client name" />
          </div>
          <div>
            <label className="input-label">Quote Expiry</label>
            <input type="date" className="input-field" value={form.quoteExpiry} onChange={e => setForm(f => ({ ...f, quoteExpiry: e.target.value }))} />
          </div>
          <div>
            <label className="input-label">Service Period</label>
            <div className="flex items-center gap-1.5">
              <input type="date" className="input-field" value={form.servicePeriodFrom} onChange={e => setForm(f => ({ ...f, servicePeriodFrom: e.target.value }))} />
              <span className="text-xs text-slate-400">To</span>
              <input type="date" className="input-field" value={form.servicePeriodTo} onChange={e => setForm(f => ({ ...f, servicePeriodTo: e.target.value }))} />
            </div>
          </div>
        </div>

        <div>
          <label className="input-label">Fund</label>
          <select className="input-field w-full sm:w-80" value={form.fund} onChange={e => setForm(f => ({ ...f, fund: e.target.value }))}>
            <option value="">Select Fund</option>
            {funds.map(f => (
              <option key={f.id} value={f.id}>
                {f.fund_name} ({money(f.fund_amount)}, {money((f.fund_amount || 0) - (f.spent_amount || 0))} remaining)
              </option>
            ))}
          </select>
        </div>

        {/* Line items table */}
        <div className="border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Pricebook</th>
                <th className="px-3 py-2.5 text-left font-semibold">Rate Group</th>
                <th className="px-3 py-2.5 text-left font-semibold">Ratio</th>
                <th className="px-3 py-2.5 text-left font-semibold">Description</th>
                <th className="px-3 py-2.5 text-left font-semibold">Discount<br/><span className="font-normal text-slate-400">% / $</span></th>
                <th className="px-3 py-2.5 text-left font-semibold">Set Recurrence</th>
                <th className="px-3 py-2.5 text-left font-semibold">Quantity</th>
                <th className="px-3 py-2.5 text-left font-semibold">Price per Unit ($)</th>
                <th className="px-3 py-2.5 text-left font-semibold">Total ($)</th>
                <th className="px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-6 text-slate-500">No lines yet. Add a price book line below.</td></tr>
              ) : lines.map((line, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2"><input className="input-field !py-1 text-xs" value={line.pricebook} onChange={e => setLine(i, 'pricebook', e.target.value)} /></td>
                  <td className="px-3 py-2"><input className="input-field !py-1 text-xs" value={line.rateGroup} onChange={e => setLine(i, 'rateGroup', e.target.value)} /></td>
                  <td className="px-3 py-2"><input className="input-field !py-1 text-xs w-16" value={line.ratio} onChange={e => setLine(i, 'ratio', e.target.value)} /></td>
                  <td className="px-3 py-2"><input className="input-field !py-1 text-xs" value={line.description} onChange={e => setLine(i, 'description', e.target.value)} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <input className="input-field !py-1 text-xs w-14" placeholder="%" value={line.discountPct} onChange={e => setLine(i, 'discountPct', e.target.value)} />
                      <input className="input-field !py-1 text-xs w-14" placeholder="$" value={line.discountDollar} onChange={e => setLine(i, 'discountDollar', e.target.value)} />
                    </div>
                  </td>
                  <td className="px-3 py-2"><input className="input-field !py-1 text-xs w-24" value={line.recurrence} onChange={e => setLine(i, 'recurrence', e.target.value)} /></td>
                  <td className="px-3 py-2"><input type="number" className="input-field !py-1 text-xs w-16" value={line.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} /></td>
                  <td className="px-3 py-2"><input type="number" className="input-field !py-1 text-xs w-20" value={line.pricePerUnit} onChange={e => setLine(i, 'pricePerUnit', e.target.value)} /></td>
                  <td className="px-3 py-2 text-slate-700 text-xs text-right tabular-nums whitespace-nowrap">{money((parseFloat(line.quantity || 0)) * (parseFloat(line.pricePerUnit || 0)))}</td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => removeLine(i)} aria-label={`Remove line ${i + 1}`} className="w-9 h-9 inline-flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"><Trash2 size={14} aria-hidden /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 border-t border-slate-100">
            <button onClick={addLine} className="text-sm text-primary-600 hover:underline flex items-center gap-1.5">
              <Plus size={14} /> Add Pricebook
            </button>
          </div>
        </div>

        <div>
          <button className="text-sm text-primary-600 hover:underline">Track change log</button>
        </div>

        {/* Quote Terms */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-2">
            <input type="checkbox" checked={form.addQuoteTerms} onChange={e => setForm(f => ({ ...f, addQuoteTerms: e.target.checked }))}
              className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-medium text-slate-700">Add {features.quote_title || 'Quote'} Terms</span>
          </label>
          {form.addQuoteTerms && (
            <textarea className="input-field w-full resize-none text-xs" rows={3}
              value={form.quoteTermsText}
              onChange={e => setForm(f => ({ ...f, quoteTermsText: e.target.value }))} />
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Link to={`/clients/${id}`} className="btn-secondary text-sm">Cancel</Link>
          <button onClick={saveQuote} disabled={saving} className="btn-primary text-sm flex items-center gap-1.5">
            {saving && <Loader2 size={13} className="animate-spin"/>}
            {saving ? 'Saving…' : 'Save Quote'}
          </button>
        </div>
      </div>
    </ClientFrame>
  );
}
